import { DealRegistryPageListDueDiligenceRecordsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DocumentIcon } from '@/assets/static/icons/document';
import { GENERATE_ANSWERS_MUTATION, GET_AI_ASSETS_QUERY } from '@/components/aiAssistant/queries';
import { IAiAssets } from '@/components/aiAssistant/types';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import { Loader } from '@/components/loaders/loader';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface AiSuggestionsProps {
  isOpen: boolean;
  currentRow: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number] | null;
  onClose: () => void;
}

const AiSuggestions = ({ isOpen, currentRow, onClose }: AiSuggestionsProps) => {
  const navigate = useNavigate();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [filesForAiAssistant, setFilesForAiAssistant] = useState<IAiAssets[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState('');

  const [generateAnswers, { loading: generateAnswersLoading }] = useMutation(GENERATE_ANSWERS_MUTATION);

  const { loading: aiAssetsLoading } = useQuery(GET_AI_ASSETS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: !currentRow,
    variables: {
      data: {
        investmentId: currentRow?.investment?.id,
        recordId: currentRow?.id,
        type: 'ddr'
      }
    },
    onCompleted: (data) => {
      if (data?.getDealAssistentAIAssets?.length) {
        const filteredAssets = data.getDealAssistentAIAssets.filter(
          (asset) => typeof asset.assetKey === 'string' && asset.assetKey.toLowerCase().endsWith('.pdf')
        );
        setFilesForAiAssistant(filteredAssets);
        setSelectedFile(data.getDealAssistentAIAssets[0]?.filename || '');
      }
    }
  });

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(event.target.value);
  };

  const handleGenerateAnswers = () => {
    try {
      setErrorMessage(null);

      if (selectedFile) {
        generateAnswers({
          variables: {
            data: {
              funnelId: currentRow?.activeTier?.funnel?.id,
              investmentId: currentRow?.investment?.id,
              recordId: currentRow?.id,
              type: 'ddr',
              filenames: [selectedFile]
            }
          }
        });
      }

      navigate(`/dueDiligence/${currentRow.id}?backToTitle=Deal Registry`, { state: { openAiPane: true } });
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  if (!currentRow) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Generate AI Suggestions"
      confirmButton={{
        name: selectedFile ? 'Confirm' : 'Open',
        disabled: generateAnswersLoading,
        onClick: handleGenerateAnswers
      }}
      showCloseIcon={!generateAnswersLoading}
      showCancelButton={!generateAnswersLoading}
      isUseOutsideClick={!generateAnswersLoading}
    >
      {generateAnswersLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalContentWrap>
            <ModalDescription>
              The AI Assistant will suggest answers to the due diligence questions based on this deal’s source material. View this deal on the Due
              Diligence Page to review the suggestions once the process is finished.
            </ModalDescription>
            {aiAssetsLoading ? (
              <Loader />
            ) : (
              <>
                <ModalDescription>
                  {selectedFile
                    ? 'Documents selected for AI Assistant:'
                    : 'No documents have been uploaded for this deal.  Open this deal to add a document before generating answers.'}
                </ModalDescription>
                <UploadedDocumnts>
                  {filesForAiAssistant.map((file) => (
                    <UploadedDocumentItem key={file.id}>
                      <RadioButtonContainer>
                        <RadioButtonWrapper>
                          <RadioButton
                            name="option"
                            radioButtonText={file.filename}
                            selectedOption={selectedFile}
                            handleRadioChange={handleRadioChange}
                            icon={<DocumentIcon fill={themeVar('font.base')} />}
                          />
                        </RadioButtonWrapper>
                      </RadioButtonContainer>
                    </UploadedDocumentItem>
                  ))}
                </UploadedDocumnts>
              </>
            )}
          </ModalContentWrap>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default AiSuggestions;

const ModalDescription = styled.div`
  font-size: 19px;
  line-height: 27px;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ModalContentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UploadedDocumnts = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px 0 0 79.5px;
  max-height: 100px;
  overflow-y: auto;
`;

const UploadedDocumentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  font-size: 16px;
  color: ${themeVar('font.base')};
  cursor: pointer;
`;

const RadioButtonContainer = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  cursor: pointer;
`;

const RadioButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
